import React, { FC, useEffect, useCallback, useState } from "react"
import { observer } from "mobx-react-lite"
import { TextStyle, Appearance, ViewStyle, TouchableOpacity, Dimensions, Alert } from "react-native"
import { Screen, Text, FlatList, ListRenderItem, JalaIcon, Checkbox } from "../../../../components"
import { useStores } from "../../../../models"
import { color } from "../../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { ScrollView, View } from "native-base"
import { translate } from "../../../../i18n"
import moment from "moment"
import numeral from 'numeral'
import _ from 'lodash'
import { ItemHeader, BottomButton } from '../simulation-harvest/simulation-harvest-screen'
import { navigate } from "../../../../navigators"
import { LineChart } from 'react-native-chart-kit';

const ROOT: ViewStyle = {
  flex: 1,
}

const SCROLL_VIEW: ViewStyle = {
  flexGrow: 1,
}

const TITLE_TEXT: TextStyle ={
  marginLeft: 16,
  marginTop: 20,
  marginBottom: 12
}

const FEED_DESC_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomColor: color.palette.gray200,
  borderBottomWidth: 1,
  paddingVertical: 10,
  paddingHorizontal: 16
}

const HIDDEN_BUTTON: ViewStyle = {
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: 10
}

const HEADER_CONTAINER = (isValue): ViewStyle => ({
  backgroundColor: isValue ? color.palette.white : color.palette.gray100,
  borderBottomWidth: 1,
  borderTopWidth: 1,
  borderColor: color.palette.lineBase,
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingVertical: 8,
  paddingHorizontal: 16
})

const LINE_CHART: ViewStyle = {
  marginVertical: 8,
  borderRadius: 16
}

const TEXT_CHART: TextStyle = {
  width: 220,
  height: 16,
  zIndex: 100,
  transform: [
    { rotate: '270deg' },
    { translateY: -78 },
    { translateX: 90 }
  ]
}

const CHART_CONTAINER: ViewStyle = {
  paddingBottom: 16,
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
}

const LEGEND_LABEL: TextStyle = { marginTop:2 }

const FeedDescription: FC<{ colorScheme?: "light"|"dark", title?: string, value?: string }> = observer(function HeaFeedDescriptionderTab(props) {
  const { title, value } = props

  return (
    <View style={FEED_DESC_CONTAINER}>
      <View width="50%">
        <Text preset="title5" text={title} color={color.palette.gray800} />
      </View>
      <View width="50%">
        <Text preset="body3" text={value} color={color.palette.gray800} />
      </View>
    </View>
  )
})

const HeaderTab: FC<{ colorScheme?: "light"|"dark" }> = observer(function HeaderTab(props) {
  return (
    <View style={HEADER_CONTAINER(false)}>
      <ItemHeader title={translate("simulationScreen.age")} width="25%" alignItems="center" isHeader />
      <ItemHeader title={translate("date")} width="45%" alignItems="center" isHeader />
      <ItemHeader title={translate("simulationScreen.FeedChartTitle")} width="30%" alignItems="flex-end" isHeader />
    </View>
  )
})

const ValueTab: FC<{ colorScheme?: "light"|"dark", data?: any, feedData?: Array<any> }> = observer(function ValueTab(props) {
  const { data, feedData } = props
  const { item, index } = data;
  const dateVal = moment(item).format('DD MMM YYYY');
  return (
    <View style={HEADER_CONTAINER(true)}>
      <ItemHeader title={`${index}`} width="20%" alignItems="center" />
      <ItemHeader title={dateVal} width="45%" alignItems="center" />
      <ItemHeader title={feedData ? numeral(feedData[index]).format('0,0.[0 ]') : '-'} width="35%" alignItems="flex-end" />
    </View>
  )
})

const ChartData: FC<{ perfect?: any, actual?: any, perfectHide?: boolean, actualHide?: boolean, axisY?: string, axisX?: string }> = observer(function ChartData(props) {
  const { actual, perfect, actualHide, perfectHide, axisX, axisY } = props
  const labels = actual.map((t, i) => `${i}     `);
  return (
    <View style={CHART_CONTAINER}>
      <View width={10}>
        <Text
          preset="body3"
          text={axisY}
          style={TEXT_CHART}
        />
      </View>
      <View alignItems="center" paddingRight={10}>
        <LineChart
          data={{
            labels,
            datasets: [
              ...(!perfectHide ? [{ data: perfect, color: () => color.palette.gray200}] : []),
              ...(!actualHide ? [{ data: actual, color: () => color.palette.green500 }] : []),
            ]
          }}
          width={Dimensions.get('window').width - 40} // from react-native
          height={240}
          chartConfig={{
            backgroundColor: color.palette.darkGrey,
            backgroundGradientFrom: color.palette.white,
            backgroundGradientTo: color.palette.white,
            decimalPlaces: 2, // optional, defaults to 2dp,
            color: () => '#B1C4D0',
            labelColor: () => color.palette.darkGrey,
            style: {
              borderRadius: 16,
            },
            propsForDots: {
              r: '4',
              strokeWidth: '1',
              stroke: '#B1C4D0'
            }
          }}
          fromZero
          withInnerLines={false}
          bezier
          hidePointsAtIndex={
            actual.map((a, i) => i).filter((i) => !(i % 20 === 0))
          }
          style={LINE_CHART}
        />
        <View alignItems="center" marginTop={-15}>
          <Text color={color.palette.gray800} preset="body3" text={axisX} />
        </View>
      </View>
    </View>
  )
})

export const SimulationFeedScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function SimulationFeedScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props

  const {
    simulationStore,
    simulationStore: { simulation }
  } = useStores()

  const [ simulationResult, setSimulationResult ] = useState<any>({})
  const [ feedShow, setFeedShow ] = useState<boolean>(false)
  const [ feedPerfectDataHide, setFeedPerfectDataHide ] = useState<boolean>(false)
  const [ feedDataHide, setFeedDataHide ] = useState<boolean>(false)
  const [ isSaving, setIsSaving ] = useState<boolean>(false)

  const initData = useCallback(async () => {
    await simulationStore.calculateSimulation(simulation).then((response) => {
      setSimulationResult(response)
    })
  }, [simulation, simulationStore])

  useEffect(() => {
    initData().catch()
  }, [initData])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <ValueTab data={info} colorScheme={colorScheme} feedData={simulationResult.feedData} />
  }, [colorScheme, simulationResult.feedData])

  const onSave = useCallback(() => {
    setIsSaving(true)
    return simulation.id
      ? simulationStore.editSimulation().then((res) => {
        if(res.status === 200 || res.status === 201 ) navigate('App.Stack', 'Home.Stack', { screen: 'Home.Simulation.List' })
        else Alert.alert('Failed', '')
      })
      : simulationStore.saveSimulation().then((res) => {
        if (res.status === 200 || res.status === 201) navigate('App.Stack', 'Home.Stack', { screen: 'Home.Simulation.List' })
        else Alert.alert('Failed', '')
      });
  }, [simulation, simulationStore])

  const onBack = useCallback(() => navigate('App.Stack', 'Home.Stack', { screen: 'Home.Simulation.List', params: 'isEdit' }), [])

  const totalFeedData = _.isEmpty(simulationResult) ? null : _.sum(simulationResult.feedData)
  const feedPrice = _.isEmpty(simulationResult) ? null : simulationResult.feedPrice
  const totalHarvest = _.isEmpty(simulationResult) ? null : simulationResult.totalHarvest
  const separatorIcon = feedShow ? 'arrow_drop_up' : 'arrow_drop_down';

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      colorScheme={colorScheme}
      backgroundColor={colorScheme === "light" ? color.palette.white : undefined}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
    >
      <ScrollView contentContainerStyle={SCROLL_VIEW}>
        <Text preset="title3" tx="overviewFeedAccumulationActualListScreen.title" colorScheme={colorScheme} style={TITLE_TEXT} color={color.palette.gray800} />
        <ChartData
          perfect={simulationResult.feedPerfectData || []}
          actual={simulationResult.feedData || []}
          perfectHide={feedPerfectDataHide}
          actualHide={feedDataHide}
          axisY={translate("simulationScreen.feedAxisY")}
          axisX={translate("simulationScreen.axisX")}
        />
        <View flex={1} alignItems="center" justifyContent="center" marginBottom="15px">
          <View>
            <Checkbox
              value={!feedPerfectDataHide}
              onToggle={() => setFeedPerfectDataHide(!feedPerfectDataHide)}
              text={translate('simulationScreen.FeedPerfectData')}
              colorScheme={colorScheme}
              addOn
              addOnColor={color.palette.gray200}
              labelStyle={LEGEND_LABEL}
              labelPreset="body2"
            />
            <Checkbox
              value={!feedDataHide}
              onToggle={() => setFeedDataHide(!feedDataHide)}
              text={translate('simulationScreen.FeedChartTitle')}
              colorScheme={colorScheme}
              addOn
              addOnColor={color.palette.green500}
              labelStyle={LEGEND_LABEL}
              labelPreset="body2"
            />
          </View>
        </View>
        <Text preset="title3" marginLeft="16px" tx="simulationScreen.FeedTabTitle" />
        <View marginBottom="20px">
          <FeedDescription title={translate("overviewFeedAccumulationActualListScreen.title")} value={`${numeral(totalFeedData).format('0,0')} kg`} />
          <FeedDescription title={translate("simulationScreen.FeedPriceTotal")} value={numeral(feedPrice * totalFeedData).format('0,0')} />
          <FeedDescription title={translate("cycleHistoryScreen.fcr")} value={totalHarvest ? numeral(totalFeedData / totalHarvest.mass).format('0,0.[00]') : '-'} />
        </View>
        <TouchableOpacity style={HIDDEN_BUTTON} onPress={() => setFeedShow(!feedShow)}>
          <Text preset="title4" color={color.primary} tx="simulationScreen.seeFeedTab" />
          <JalaIcon name={separatorIcon} size={17} color={color.primary} />
        </TouchableOpacity>
        {feedShow &&
          <View>
            <HeaderTab />
            <View>
              <FlatList
                data={simulationResult.cultivationDates}
                renderItem={renderItem}
                estimatedItemSize={67}
              />
            </View>
          </View>
        }
      </ScrollView>
      <BottomButton onSave={onSave} onBack={onBack} colorScheme={colorScheme} disabled={isSaving} />
    </Screen>
  )
})
